import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import {
  estimateImportCost,
  formatImportCost
} from "../../../../utils/floorplanImportCost";
import type { FloorplanComparison } from "../../../../utils/requestFloorplanComparison";
import { ImportNotes } from "./ImportNotes";

export function FloorplanImportStatus({
  snapshot
}: {
  snapshot: FloorplanComparison;
}) {
  const candidate = snapshot.candidates[0];
  if (!candidate) return null;
  const cost = estimateImportCost(candidate, snapshot.startedAt);
  const completed = candidate.status === "completed";
  const pending = candidate.status === "in_progress";
  const ready = candidate.areas.filter((a) => a.status === "completed").length;
  const reused = candidate.areas.filter((a) => a.cached).length;
  const hedged = candidate.areas.filter((a) => a.hedged).length;
  const speedNotes = [
    reused ? `${reused} reused from an earlier run` : "",
    hedged
      ? `${hedged} slow part${hedged === 1 ? "" : "s"} started a second attempt`
      : ""
  ].filter(Boolean);
  const errors = [
    ...new Set(
      [
        candidate.error,
        ...candidate.areas.map((a) =>
          a.error ? `${a.label}: ${a.error}` : undefined
        )
      ].filter((error): error is string => !!error)
    )
  ];
  return (
    <section
      className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-4"
      aria-label="Import progress"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {completed ? (
            <CheckCircle2 size={20} className="text-green-600" />
          ) : pending ? (
            <Loader2
              size={20}
              className="animate-spin text-blue-600 motion-reduce:animate-none"
            />
          ) : (
            <AlertCircle size={20} className="text-red-600" />
          )}
          <div>
            <p className="font-medium text-neutral-900 text-sm">
              {completed
                ? "Your map is ready to review"
                : pending
                  ? "Building your map"
                  : "We couldn’t finish this map"}
            </p>
            <p className="text-neutral-500 text-xs">
              {pending
                ? `${ready} of ${candidate.areas.length} sections complete`
                : `${Math.floor(candidate.elapsedMs / 60000)}m ${Math.floor(candidate.elapsedMs / 1000) % 60}s elapsed`}
            </p>
          </div>
        </div>
        <p className="text-right text-neutral-500 text-xs">
          Estimated cost
          <span className="block font-medium text-neutral-900 text-sm">
            {formatImportCost(cost.usd)} USD{cost.partial ? " so far" : ""}
          </span>
        </p>
      </div>
      {speedNotes.length > 0 && (
        <p className="mt-2 text-neutral-500 text-xs">
          {speedNotes.join(" · ")}
        </p>
      )}
      {pending && candidate.areas.length > 0 && (
        <div
          role="progressbar"
          aria-label="Sections completed"
          aria-valuemin={0}
          aria-valuemax={candidate.areas.length}
          aria-valuenow={ready}
          className="mt-4 h-1.5 overflow-hidden rounded-full bg-neutral-200"
        >
          <div
            className="h-full rounded-full bg-blue-500 transition-all motion-reduce:transition-none"
            style={{ width: `${(100 * ready) / candidate.areas.length}%` }}
          />
        </div>
      )}
      {errors.length > 0 && (
        <div className="mt-3">
          <ImportNotes
            tone="error"
            items={errors}
            title={`${errors.length} part${errors.length === 1 ? "" : "s"} ${pending ? "hit a problem" : "failed"}`}
          />
        </div>
      )}
      <details className="mt-3 text-neutral-500 text-xs">
        <summary className="cursor-pointer">Import details</summary>
        <p className="mt-2">
          Cost is estimated from reported usage. Actual billing may differ.
        </p>
        <button
          type="button"
          className="mt-2 underline"
          onClick={() => {
            const url = URL.createObjectURL(
              new Blob(
                [JSON.stringify({ candidate, estimatedCost: cost }, null, 2)],
                { type: "application/json" }
              )
            );
            const link = document.createElement("a");
            link.href = url;
            link.download = "floorplan-import-report.json";
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          Download report
        </button>
      </details>
    </section>
  );
}
